import React, { createContext, useContext, useState, useEffect, useRef, PropsWithChildren } from 'react';

export interface SidebarOverrideItem {
    key: string;
    label: string;
    icon: string; // Icon key from IconManager (e.g. 'cog_solid')
}

export interface SidebarMenuOverride {
    backLabel: string;
    items: SidebarOverrideItem[];
    selectedKey: string;
    onBackClick: () => void;
    onItemClick: (key: string) => void;
}

interface SidebarMenuContextValue {
    override: SidebarMenuOverride | null;
    setOverride: (override: SidebarMenuOverride | null) => void;
}

const SidebarMenuContext = createContext<SidebarMenuContextValue>({
    override: null,
    setOverride: () => {}
});

export const SidebarMenuProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [override, setOverride] = useState<SidebarMenuOverride | null>(null);

    return <SidebarMenuContext.Provider value={{ override, setOverride }}>{children}</SidebarMenuContext.Provider>;
};

/**
 * Hook for page components to override the sidebar menu.
 * Sets the override on mount/update and clears it on unmount.
 *
 * Uses a ref for callbacks to avoid unnecessary re-renders while
 * keeping the override reactive to selectedKey changes.
 */
export const useSidebarOverride = (config: SidebarMenuOverride) => {
    const { setOverride } = useContext(SidebarMenuContext);
    const configRef = useRef(config);
    configRef.current = config;

    useEffect(() => {
        setOverride({
            ...configRef.current,
            onBackClick: () => configRef.current.onBackClick(),
            onItemClick: (key: string) => configRef.current.onItemClick(key)
        });
    }, [config.selectedKey, config.backLabel, config.items, setOverride]);

    useEffect(() => {
        return () => setOverride(null);
    }, [setOverride]);
};

/** Hook for the sidebar to read the current override */
export const useSidebarMenuContext = () => useContext(SidebarMenuContext);
